/**
 * ConfirmationCard Component
 * Shows a confirmation dialog when the AI wants to perform an action
 * that requires user approval before execution.
 */

import React from 'react';
import { useAIContext } from './AIContext';
import { useThemeContext } from '../../contexts/ThemeContext';

const ConfirmationCard: React.FC = () => {
  const { pendingConfirmation, confirmAction, cancelAction, isProcessing } = useAIContext();
  const { isDark } = useThemeContext();

  if (!pendingConfirmation) return null;

  return (
    <div className="p-3 border-t" style={{ borderColor: 'var(--grey-light)' }}>
      <div
        className="rounded-xl p-4"
        style={{
          backgroundColor: 'var(--bg-grey-light)',
          border: '1px solid',
          borderColor: 'var(--secondary)',
        }}
      >
        {/* Warning header */}
        <div className="flex items-center gap-2 mb-3">
          <span className="font-semibold text-sm" style={{ color: 'var(--grey-dark)' }}>
            Confirm Action
          </span>
        </div>

        {/* Action description */}
        <div
          className="text-sm mb-4 p-3 rounded-lg whitespace-pre-wrap"
          style={{
            backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
            color: 'var(--dark)',
          }}
        >
          {renderInlineMarkdown(pendingConfirmation.humanReadableAction)}
        </div>

        {/* Action hint */}
        <div className="text-xs mb-4 opacity-70" style={{ color: 'var(--grey-dark)' }}>
          This action will modify your data. Please review carefully before confirming.
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={confirmAction}
            disabled={isProcessing}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--primary)',
              color: '#FFFFFF',
            }}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Executing...
              </span>
            ) : (
              'Confirm'
            )}
          </button>
          <button
            onClick={cancelAction}
            disabled={isProcessing}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--grey-light)',
              color: 'var(--dark)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationCard;

/**
 * Simple inline markdown renderer for bold (**text**) and line breaks
 */
function renderInlineMarkdown(text: string): React.ReactNode {
  if (!text) return null;

  const parts = text.split(/(\*\*[\s\S]*?\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part.split('\n').map((line, lineIdx, arr) => (
      <React.Fragment key={`${idx}-${lineIdx}`}>
        {line}
        {lineIdx < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}
